/**
 * Where each mould currently is.
 *
 * Location is deliberately *not* a column on the mould record. A mould's place
 * is whatever the last scan says, so storing it would mean two sources of truth
 * that drift the moment somebody moves a tool without editing a form. The
 * server derives it from the scan ledger, which is not replicated to the device.
 *
 * That leaves the catalog - an offline-first screen - needing an online-only
 * value. So the answer is cached: fetched whenever there is a connection, kept
 * in localStorage, and served from there when there is not. A location that is
 * a few hours stale is worth far more to someone on the floor than an empty
 * column, and the detail page always has the authoritative history.
 */
import { useEffect, useState } from 'react';
import { api, isNetworkError } from '../../api/client.js';
import { isCheckOut, transitOrigin, type LiveBoardRow } from '@mouldtrack/shared';

const CACHE_KEY = 'mt_mould_locations';

export interface MouldPlace {
  location: string | null;
  store: string | null;
  lastScannedAt: string | null;
  /** The kind of checkpoint it was last scanned at. Missing from a cache written before 11 Oct 2026. */
  checkpointType?: string | null;
}

/** assetCode (upper-cased) → where it was last seen. */
export type PlaceMap = Record<string, MouldPlace>;

function readCache(): PlaceMap {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? (parsed as PlaceMap) : {};
  } catch {
    return {}; // a corrupt cache is a missing cache, never a crash
  }
}

/**
 * Where one mould is, out of the map.
 *
 * Keyed by asset code rather than id because that is what the scan ledger
 * carries. Lived in the catalog page until the dashboard needed the same
 * lookup to work out how much of the catalog has been seen lately.
 */
export function placeOf(
  places: PlaceMap,
  mould: { assetCode?: string | null },
): MouldPlace | undefined {
  const key = mould.assetCode?.trim().toUpperCase();
  return key ? places[key] : undefined;
}

/**
 * What to say about where a mould is, given its status.
 *
 * A Mold OUT puts a mould IN_TRANSIT, and the ledger's last scan is still the
 * check-out at the site it left - so the place on its own ("Mira Road") would
 * be wrong for as long as it is on the road. In transit, the place is where it
 * came from ("from Mira Road"), or where it was last recorded on the way
 * ("last seen at Andheri"). A Mold IN at the destination ends that: the status
 * goes back to In store and the place is the destination.
 *
 * `origin` is that second half on its own, for a column with room for one word
 * on top; `leftFrom` is the site it was checked out of, when the check-out is
 * still its latest scan.
 */
export function whereabouts(
  mould: { status?: string | null },
  place: MouldPlace | undefined,
): { inTransit: boolean; label: string | null; origin: string | null; leftFrom: string | null } {
  const at = place?.location ? `${place.location}${place.store ? ` · ${place.store}` : ''}` : null;
  if (mould.status !== 'IN_TRANSIT') return { inTransit: false, label: at, origin: null, leftFrom: null };
  const origin = transitOrigin(at, place?.checkpointType);
  return {
    inTransit: true,
    label: origin ? `In transit · ${origin}` : 'In transit',
    origin,
    leftFrom: isCheckOut(place?.checkpointType) ? (place?.location ?? null) : null,
  };
}

export function useMouldLocations(): PlaceMap {
  const [places, setPlaces] = useState<PlaceMap>(readCache);

  useEffect(() => {
    let cancelled = false;

    const load = () => {
      if (!navigator.onLine) return;
      void api
        .liveBoard()
        .then((rows: LiveBoardRow[]) => {
          if (cancelled) return;
          const next: PlaceMap = {};
          for (const r of rows) {
            if (!r.assetCode) continue;
            next[r.assetCode.trim().toUpperCase()] = {
              location: r.location,
              store: r.store,
              lastScannedAt: r.lastScannedAt,
              checkpointType: r.checkpointType,
            };
          }
          setPlaces(next);
          try {
            localStorage.setItem(CACHE_KEY, JSON.stringify(next));
          } catch {
            // Storage full or blocked - the in-memory copy still works today.
          }
        })
        .catch((e: unknown) => {
          // Offline or a hiccup: keep showing the cache. Anything else is worth
          // knowing about but still not worth breaking the catalog over.
          if (!isNetworkError(e)) console.warn('live board unavailable', e);
        });
    };

    load();
    window.addEventListener('online', load);
    return () => {
      cancelled = true;
      window.removeEventListener('online', load);
    };
  }, []);

  return places;
}
